import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  AdminCompleteFailedPartnerDriverStep,
  AdminCompletePartnerDriverBackgroundCheck,
  AdminCompletePartnerDriverDotNumber,
  AdminCompletePartnerDriverInsuranceDeclaration,
  AdminCompletePartnerDriverLicense,
  AdminCompletePartnerDriverLmsTraining,
  AdminCompletePartnerDriverVehicleInsurance,
  AdminCompletePartnerDriverVehicleRegistration,
  AdminDeletePartnerDriverData,
  AdminDeletePartnerDriverStepDocuments,
  AdminFetchPartnerDriver,
  AdminFetchPartnerDriverSummary,
  AdminResetPartnerDriverStep,
  AdminSearchPartnerDrivers,
  AdminSkipPartnerDriverStep,
  AdminSwitchPartnerDriverOnboarding,
  AdminUpdatePartnerDriver,
  AdminUpdatePartnerDriverTemplate,
  AdminUploadPartnerDriverStepDocuments,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import { STEPS_SOURCE } from 'src/constants/partnerDriver/constants'
import type {
  CompleteDotNumberPayload,
  CompleteDriverLicensePayload,
  CompleteVehicleInsurancePayload,
  DeletePartnerDriverDataPayload,
  PartnerDriverDetails,
  PartnerDriverIdPayload,
  PartnerDriverSearchRow,
  PartnerDriverStepFilesPayload,
  PartnerDriverStepPayload,
  ResetPartnerDriverStepPayload,
  SearchPartnerDriversPayload,
  SwitchPartnerDriverOnboardingPayload,
  UpdatePartnerDriverPayload,
  UpdatePartnerDriverTemplatePayload,
} from 'src/interfaces/partnerDriver'
import { normalizeApiListPayload } from 'src/utils/normalizeApiListPayload'
import { parseJsonBody } from 'src/utils/partnerDriver/parseJsonBody'
import { hasSearchQuery } from 'src/utils/partnerDriver/searchQuery'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'
import type { UrlReplaceParams } from './api/callApi'

const EMPTY_SEARCH_MESSAGE = 'Enter an email, phone or partner driver ID'

const partnerDriverIdParams = (partnerDriverId: string): UrlReplaceParams[] => {
  return [{ field: ':partnerDriverId', value: partnerDriverId }]
}

const stepIdParams = (stepId: string): UrlReplaceParams[] => {
  return [{ field: ':stepId', value: stepId }]
}

const buildFilesFormData = (files: File[]): FormData => {
  const formData = new FormData()

  files.forEach((file) => {
    formData.append('files', file)
  })

  return formData
}

// `callApi` builds the query with `qs.stringify(…, { encode: false })`, so a raw `+` would reach
// the server as a space. Encode here, once.
const encodeSearchQuery = (query: SearchPartnerDriversPayload): Record<string, string> => {
  const encoded: Record<string, string> = {}

  Object.entries(query).forEach(([key, value]) => {
    if (typeof value === 'string' && value !== '') {
      encoded[key] = encodeURIComponent(value)
    }
  })

  return encoded
}

export function* onSearchPartnerDrivers(action: AsyncAction<SearchPartnerDriversPayload>) {
  const query = action.payload ?? {}

  if (!hasSearchQuery(query)) {
    yield put(AdminSearchPartnerDrivers.failed(new Error(EMPTY_SEARCH_MESSAGE)))

    if (action.next) {
      action.next(EMPTY_SEARCH_MESSAGE)
    }

    return
  }

  try {
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.InternalContractorsSearch,
      {
        method: 'GET',
        query: encodeSearchQuery(query),
      },
    )
    const rows = normalizeApiListPayload<PartnerDriverSearchRow>(payload)

    yield put(AdminSearchPartnerDrivers.success(rows))

    if (action.next) {
      action.next(null, rows)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.partnerDriver.onSearchPartnerDrivers] error' })

    yield put(AdminSearchPartnerDrivers.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to search drivers'))
    }
  }
}

export function* onFetchPartnerDriver(action: AsyncAction<Partial<PartnerDriverIdPayload>>) {
  const partnerDriverId = action.payload?.partnerDriverId

  if (!partnerDriverId) {
    yield put(AdminFetchPartnerDriver.failed(new Error('Missing partnerDriverId')))

    if (action.next) {
      action.next('Missing partnerDriverId')
    }

    return
  }

  try {
    // Text, not JSON: an unknown driver is a 200 with an empty body. See parseJsonBody.
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.InternalContractorSteps,
      {
        method: 'GET',
        params: partnerDriverIdParams(partnerDriverId),
        query: { source: STEPS_SOURCE },
        returnResponse: true,
      },
    )
    const driver = parseJsonBody<PartnerDriverDetails>(payload)

    yield put(AdminFetchPartnerDriver.success({ partnerDriverId, driver }))

    if (action.next) {
      action.next(null, driver)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.partnerDriver.onFetchPartnerDriver] error' })

    yield put(AdminFetchPartnerDriver.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load the driver'))
    }
  }
}

export function* onFetchPartnerDriverSummary(action: AsyncAction<Partial<PartnerDriverIdPayload>>) {
  const partnerDriverId = action.payload?.partnerDriverId

  if (!partnerDriverId) {
    yield put(AdminFetchPartnerDriverSummary.failed(new Error('Missing partnerDriverId')))

    if (action.next) {
      action.next('Missing partnerDriverId')
    }

    return
  }

  try {
    // `partnerDriverId` is an exact match server-side and a UUID, so it needs no encoding.
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.InternalContractorsSearch,
      {
        method: 'GET',
        query: { partnerDriverId },
      },
    )
    const summary =
      normalizeApiListPayload<PartnerDriverSearchRow>(payload).find((row) => {
        return row.id === partnerDriverId
      }) ?? null

    yield put(AdminFetchPartnerDriverSummary.success(summary))

    if (action.next) {
      action.next(null, summary)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.partnerDriver.onFetchPartnerDriverSummary] error' })

    yield put(AdminFetchPartnerDriverSummary.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load background checks'))
    }
  }
}

export function* onUpdatePartnerDriver(action: AsyncAction<Partial<UpdatePartnerDriverPayload>>) {
  const partnerDriverId = action.payload?.partnerDriverId

  if (!partnerDriverId) {
    yield put(AdminUpdatePartnerDriver.failed(new Error('Missing partnerDriverId')))

    if (action.next) {
      action.next('Missing partnerDriverId')
    }

    return
  }

  try {
    // The DTO has no whitelist — the caller builds `body` from UpdatePartnerDriverBody only.
    yield call(callSecureApi, Endpoints.InternalContractor, {
      method: 'PUT',
      params: partnerDriverIdParams(partnerDriverId),
      body: action.payload?.body ?? {},
      returnResponse: true,
    })

    yield put(AdminUpdatePartnerDriver.success())

    if (action.next) {
      action.next(null, true)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.partnerDriver.onUpdatePartnerDriver] error' })

    yield put(AdminUpdatePartnerDriver.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to update the driver'))
    }
  }
}

export function* onUpdatePartnerDriverTemplate(
  action: AsyncAction<Partial<UpdatePartnerDriverTemplatePayload>>,
) {
  const partnerDriverId = action.payload?.partnerDriverId
  const collectDataTemplateId = action.payload?.collectDataTemplateId

  if (!partnerDriverId || !collectDataTemplateId) {
    yield put(AdminUpdatePartnerDriverTemplate.failed(new Error('Missing template change ids')))

    if (action.next) {
      action.next('Missing template change ids')
    }

    return
  }

  try {
    yield call(callSecureApi, Endpoints.InternalContractorTemplate, {
      method: 'PUT',
      params: partnerDriverIdParams(partnerDriverId),
      body: { collectDataTemplateId },
      returnResponse: true,
    })

    yield put(AdminUpdatePartnerDriverTemplate.success())

    if (action.next) {
      action.next(null, true)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.partnerDriver.onUpdatePartnerDriverTemplate] error',
    })

    yield put(AdminUpdatePartnerDriverTemplate.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to change the template'))
    }
  }
}

export function* onSwitchPartnerDriverOnboarding(
  action: AsyncAction<Partial<SwitchPartnerDriverOnboardingPayload>>,
) {
  const partnerDriverId = action.payload?.partnerDriverId
  const onboarded = action.payload?.onboarded

  if (!partnerDriverId || typeof onboarded !== 'boolean') {
    yield put(AdminSwitchPartnerDriverOnboarding.failed(new Error('Missing onboarding status')))

    if (action.next) {
      action.next('Missing onboarding status')
    }

    return
  }

  try {
    // `{ onboarded }` only: the DTO forbids unknown keys and the actor comes from the token.
    yield call(callSecureApi, Endpoints.InternalContractorOnboardingStatus, {
      method: 'POST',
      params: partnerDriverIdParams(partnerDriverId),
      body: { onboarded },
      returnResponse: true,
    })

    yield put(AdminSwitchPartnerDriverOnboarding.success())

    if (action.next) {
      action.next(null, true)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.partnerDriver.onSwitchPartnerDriverOnboarding] error',
    })

    yield put(AdminSwitchPartnerDriverOnboarding.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to update onboarding status'))
    }
  }
}

export function* onResetPartnerDriverStep(
  action: AsyncAction<Partial<ResetPartnerDriverStepPayload>>,
) {
  const partnerDriverId = action.payload?.partnerDriverId
  const stepId = action.payload?.stepId

  if (!partnerDriverId || !stepId) {
    yield put(AdminResetPartnerDriverStep.failed(new Error('Missing stepId')))

    if (action.next) {
      action.next('Missing stepId')
    }

    return
  }

  try {
    yield call(callSecureApi, Endpoints.InternalContractorStepReset, {
      method: 'POST',
      params: stepIdParams(stepId),
      body: { partnerDriverId },
      returnResponse: true,
    })

    yield put(AdminResetPartnerDriverStep.success())

    if (action.next) {
      action.next(null, true)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.partnerDriver.onResetPartnerDriverStep] error' })

    yield put(AdminResetPartnerDriverStep.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to reset the step'))
    }
  }
}

export function* onCompleteFailedPartnerDriverStep(
  action: AsyncAction<Partial<PartnerDriverStepPayload>>,
) {
  const stepId = action.payload?.stepId

  if (!stepId) {
    yield put(AdminCompleteFailedPartnerDriverStep.failed(new Error('Missing stepId')))

    if (action.next) {
      action.next('Missing stepId')
    }

    return
  }

  try {
    yield call(callSecureApi, Endpoints.InternalContractorStepCompleteFailed, {
      method: 'POST',
      params: stepIdParams(stepId),
      returnResponse: true,
    })

    yield put(AdminCompleteFailedPartnerDriverStep.success())

    if (action.next) {
      action.next(null, true)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.partnerDriver.onCompleteFailedPartnerDriverStep] error',
    })

    yield put(AdminCompleteFailedPartnerDriverStep.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to complete the step'))
    }
  }
}

export function* onDeletePartnerDriverStepDocuments(
  action: AsyncAction<Partial<PartnerDriverStepPayload>>,
) {
  const stepId = action.payload?.stepId

  if (!stepId) {
    yield put(AdminDeletePartnerDriverStepDocuments.failed(new Error('Missing stepId')))

    if (action.next) {
      action.next('Missing stepId')
    }

    return
  }

  try {
    yield call(callSecureApi, Endpoints.InternalContractorStepDocuments, {
      method: 'DELETE',
      params: stepIdParams(stepId),
      returnResponse: true,
    })

    yield put(AdminDeletePartnerDriverStepDocuments.success())

    if (action.next) {
      action.next(null, true)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.partnerDriver.onDeletePartnerDriverStepDocuments] error',
    })

    yield put(AdminDeletePartnerDriverStepDocuments.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to delete the documents'))
    }
  }
}

// Upload, background check, LMS training and insurance declaration differ only in endpoint,
// action creators and messages; one factory keeps their wire contract identical.
const buildStepFilesHandler = (config: {
  endpoint: Endpoints
  actions: typeof AdminUploadPartnerDriverStepDocuments
  sentryMessage: string
  failureMessage: string
}) => {
  return function* (action: AsyncAction<Partial<PartnerDriverStepFilesPayload>>) {
    const stepId = action.payload?.stepId
    const files = action.payload?.files ?? []

    if (!stepId || files.length === 0) {
      yield put(config.actions.failed(new Error('Missing stepId or files')))

      if (action.next) {
        action.next('Missing stepId or files')
      }

      return
    }

    try {
      yield call(callSecureApi, config.endpoint, {
        method: 'POST',
        params: stepIdParams(stepId),
        body: buildFilesFormData(files),
        resetContentType: true,
        returnResponse: true,
      })

      yield put(config.actions.success())

      if (action.next) {
        action.next(null, true)
      }
    } catch (error: unknown) {
      captureSentryError(error, { message: config.sentryMessage })

      yield put(config.actions.failed(error))

      if (action.next) {
        action.next(resolveErrorMessage(error, config.failureMessage))
      }
    }
  }
}

export const onUploadPartnerDriverStepDocuments = buildStepFilesHandler({
  endpoint: Endpoints.InternalContractorStepDocuments,
  actions: AdminUploadPartnerDriverStepDocuments,
  sentryMessage: '[saga.partnerDriver.onUploadPartnerDriverStepDocuments] error',
  failureMessage: 'Failed to upload the documents',
})

export const onCompletePartnerDriverBackgroundCheck = buildStepFilesHandler({
  endpoint: Endpoints.InternalContractorStepCompleteBackgroundCheck,
  actions: AdminCompletePartnerDriverBackgroundCheck,
  sentryMessage: '[saga.partnerDriver.onCompletePartnerDriverBackgroundCheck] error',
  failureMessage: 'Failed to complete the background check',
})

export function* onCompletePartnerDriverVehicleInsurance(
  action: AsyncAction<Partial<CompleteVehicleInsurancePayload>>,
) {
  const payload = action.payload
  const stepId = payload?.stepId

  if (!payload || !stepId || !payload.insuranceDocument || !payload.vehicleType) {
    yield put(
      AdminCompletePartnerDriverVehicleInsurance.failed(new Error('Missing vehicle details')),
    )

    if (action.next) {
      action.next('Missing vehicle details')
    }

    return
  }

  try {
    const formData = new FormData()

    formData.append('file', payload.insuranceDocument)

    if (payload.policyDocument) {
      formData.append('policyDocument', payload.policyDocument)
    }

    // The body `stepId` is the step the service validates and finishes; the URL one is where the
    // documents land. They must be the same id.
    formData.append('stepId', stepId)
    formData.append('type', payload.vehicleType)

    // Blank optional fields are omitted rather than sent as '' (micro-mobility types).
    const optionalFields: Array<[string, string | undefined]> = [
      ['year', payload.year],
      ['carMake', payload.carMake],
      ['carModel', payload.carModel],
      ['vin', payload.vin],
    ]

    optionalFields.forEach(([field, value]) => {
      const trimmed = (value ?? '').trim()

      if (trimmed !== '') {
        formData.append(field, trimmed)
      }
    })

    yield call(callSecureApi, Endpoints.InternalContractorStepCompleteVehicleInsurance, {
      method: 'POST',
      params: stepIdParams(stepId),
      body: formData,
      resetContentType: true,
      returnResponse: true,
    })

    yield put(AdminCompletePartnerDriverVehicleInsurance.success())

    if (action.next) {
      action.next(null, true)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.partnerDriver.onCompletePartnerDriverVehicleInsurance] error',
    })

    yield put(AdminCompletePartnerDriverVehicleInsurance.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to complete vehicle & insurance'))
    }
  }
}

export function* onCompletePartnerDriverLicense(
  action: AsyncAction<Partial<CompleteDriverLicensePayload>>,
) {
  const stepId = action.payload?.stepId
  const file = action.payload?.file
  const expiresAt = action.payload?.expiresAt

  if (!stepId || !file || !expiresAt) {
    yield put(AdminCompletePartnerDriverLicense.failed(new Error('Missing license details')))

    if (action.next) {
      action.next('Missing license details')
    }

    return
  }

  try {
    const formData = buildFilesFormData([file])

    formData.append('expiresAt', expiresAt)

    yield call(callSecureApi, Endpoints.InternalContractorStepCompleteDriverLicense, {
      method: 'POST',
      params: stepIdParams(stepId),
      body: formData,
      resetContentType: true,
      returnResponse: true,
    })

    yield put(AdminCompletePartnerDriverLicense.success())

    if (action.next) {
      action.next(null, true)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.partnerDriver.onCompletePartnerDriverLicense] error',
    })

    yield put(AdminCompletePartnerDriverLicense.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to complete the driver license'))
    }
  }
}

export const onCompletePartnerDriverLmsTraining = buildStepFilesHandler({
  endpoint: Endpoints.InternalContractorStepCompleteLmsTraining,
  actions: AdminCompletePartnerDriverLmsTraining,
  sentryMessage: '[saga.partnerDriver.onCompletePartnerDriverLmsTraining] error',
  failureMessage: 'Failed to complete the LMS training',
})

export const onCompletePartnerDriverInsuranceDeclaration = buildStepFilesHandler({
  endpoint: Endpoints.InternalContractorStepCompleteInsuranceDeclaration,
  actions: AdminCompletePartnerDriverInsuranceDeclaration,
  sentryMessage: '[saga.partnerDriver.onCompletePartnerDriverInsuranceDeclaration] error',
  failureMessage: 'Failed to complete the insurance declaration',
})

// ENG-884: one file under `files`, the same wire shape as the other file completions.
export const onCompletePartnerDriverVehicleRegistration = buildStepFilesHandler({
  endpoint: Endpoints.InternalContractorStepCompleteVehicleRegistration,
  actions: AdminCompletePartnerDriverVehicleRegistration,
  sentryMessage: '[saga.partnerDriver.onCompletePartnerDriverVehicleRegistration] error',
  failureMessage: 'Failed to complete the vehicle registration',
})

const MISSING_DOT_NUMBER_DETAILS = 'Missing DOT number details'

export function* onCompletePartnerDriverDotNumber(
  action: AsyncAction<Partial<CompleteDotNumberPayload>>,
) {
  const payload = action.payload
  const stepId = payload?.stepId
  const hasDotNumber = Boolean(payload?.dotNumber)
  const hasUsDotNumber = Boolean(payload?.usDotNumber)

  // Exactly one number: the backend rejects both, and a DOT number needs its state.
  if (
    !payload ||
    !stepId ||
    !payload.lastSubmittedAt ||
    hasDotNumber === hasUsDotNumber ||
    (hasDotNumber && !payload.stateOfIssue)
  ) {
    yield put(AdminCompletePartnerDriverDotNumber.failed(new Error(MISSING_DOT_NUMBER_DETAILS)))

    if (action.next) {
      action.next(MISSING_DOT_NUMBER_DETAILS)
    }

    return
  }

  try {
    const formData = new FormData()

    if (payload.dotNumber && payload.stateOfIssue) {
      formData.append('dotNumber', payload.dotNumber)
      formData.append('stateOfIssue', payload.stateOfIssue)
    }

    if (payload.usDotNumber) {
      formData.append('usDotNumber', payload.usDotNumber)
    }

    formData.append('lastSubmittedAt', payload.lastSubmittedAt)

    // Optional server-side (multer field `file`); the form enforces the step's medical card rule.
    if (payload.file) {
      formData.append('file', payload.file)
    }

    yield call(callSecureApi, Endpoints.InternalContractorStepCompleteDotNumber, {
      method: 'POST',
      params: stepIdParams(stepId),
      body: formData,
      resetContentType: true,
      returnResponse: true,
    })

    yield put(AdminCompletePartnerDriverDotNumber.success())

    if (action.next) {
      action.next(null, true)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.partnerDriver.onCompletePartnerDriverDotNumber] error',
    })

    yield put(AdminCompletePartnerDriverDotNumber.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to complete the DOT number'))
    }
  }
}

export function* onSkipPartnerDriverStep(action: AsyncAction<Partial<PartnerDriverStepPayload>>) {
  const stepId = action.payload?.stepId

  if (!stepId) {
    yield put(AdminSkipPartnerDriverStep.failed(new Error('Missing stepId')))

    if (action.next) {
      action.next('Missing stepId')
    }

    return
  }

  try {
    yield call(callSecureApi, Endpoints.InternalContractorStepSkip, {
      method: 'POST',
      params: stepIdParams(stepId),
      returnResponse: true,
    })

    yield put(AdminSkipPartnerDriverStep.success())

    if (action.next) {
      action.next(null, true)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.partnerDriver.onSkipPartnerDriverStep] error' })

    yield put(AdminSkipPartnerDriverStep.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to skip the step'))
    }
  }
}

const MISSING_DELETION_REASON = 'Missing deletion reason'

export function* onDeletePartnerDriverData(
  action: AsyncAction<Partial<DeletePartnerDriverDataPayload>>,
) {
  const partnerDriverId = action.payload?.partnerDriverId
  const reason = action.payload?.reason

  if (!partnerDriverId || !reason) {
    yield put(AdminDeletePartnerDriverData.failed(new Error(MISSING_DELETION_REASON)))

    if (action.next) {
      action.next(MISSING_DELETION_REASON)
    }

    return
  }

  try {
    // `{ reason }` only: the DTO forbids unknown keys and the actor comes from the token.
    yield call(callSecureApi, Endpoints.InternalContractorDeleteData, {
      method: 'POST',
      params: partnerDriverIdParams(partnerDriverId),
      body: { reason },
      returnResponse: true,
    })

    yield put(AdminDeletePartnerDriverData.success({ partnerDriverId }))

    if (action.next) {
      action.next(null, true)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.partnerDriver.onDeletePartnerDriverData] error' })

    yield put(AdminDeletePartnerDriverData.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to delete the contractor'))
    }
  }
}

export function* partnerDriverSaga() {
  // Reads are re-triggered by typing, refresh and refetch-after-write; only the newest matters.
  yield takeLatest(AdminSearchPartnerDrivers.type.REQUEST, onSearchPartnerDrivers)
  yield takeLatest(AdminFetchPartnerDriver.type.REQUEST, onFetchPartnerDriver)
  yield takeLatest(AdminFetchPartnerDriverSummary.type.REQUEST, onFetchPartnerDriverSummary)
  // Every write must run — takeLatest would drop one fired in quick succession.
  yield takeEvery(AdminUpdatePartnerDriver.type.REQUEST, onUpdatePartnerDriver)
  yield takeEvery(AdminUpdatePartnerDriverTemplate.type.REQUEST, onUpdatePartnerDriverTemplate)
  yield takeEvery(AdminSwitchPartnerDriverOnboarding.type.REQUEST, onSwitchPartnerDriverOnboarding)
  yield takeEvery(AdminResetPartnerDriverStep.type.REQUEST, onResetPartnerDriverStep)
  yield takeEvery(
    AdminCompleteFailedPartnerDriverStep.type.REQUEST,
    onCompleteFailedPartnerDriverStep,
  )
  yield takeEvery(
    AdminUploadPartnerDriverStepDocuments.type.REQUEST,
    onUploadPartnerDriverStepDocuments,
  )
  yield takeEvery(
    AdminDeletePartnerDriverStepDocuments.type.REQUEST,
    onDeletePartnerDriverStepDocuments,
  )
  yield takeEvery(
    AdminCompletePartnerDriverBackgroundCheck.type.REQUEST,
    onCompletePartnerDriverBackgroundCheck,
  )
  yield takeEvery(
    AdminCompletePartnerDriverVehicleInsurance.type.REQUEST,
    onCompletePartnerDriverVehicleInsurance,
  )
  yield takeEvery(AdminCompletePartnerDriverLicense.type.REQUEST, onCompletePartnerDriverLicense)
  yield takeEvery(
    AdminCompletePartnerDriverLmsTraining.type.REQUEST,
    onCompletePartnerDriverLmsTraining,
  )
  yield takeEvery(
    AdminCompletePartnerDriverInsuranceDeclaration.type.REQUEST,
    onCompletePartnerDriverInsuranceDeclaration,
  )
  yield takeEvery(
    AdminCompletePartnerDriverVehicleRegistration.type.REQUEST,
    onCompletePartnerDriverVehicleRegistration,
  )
  yield takeEvery(
    AdminCompletePartnerDriverDotNumber.type.REQUEST,
    onCompletePartnerDriverDotNumber,
  )
  yield takeEvery(AdminSkipPartnerDriverStep.type.REQUEST, onSkipPartnerDriverStep)
  yield takeEvery(AdminDeletePartnerDriverData.type.REQUEST, onDeletePartnerDriverData)
}
